import React, { useEffect, useMemo, useState } from "react";
import Card from "components/card";
import {
  FaChevronLeft,
  FaChevronRight,
  FaRegCalendarTimes,
  FaListUl,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { fetchMyReservations } from "api";

// --- helpers ---
const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "";
const formatTimeRange = (startIso, endIso) => {
  const s = startIso ? new Date(startIso) : null, e = endIso ? new Date(endIso) : null;
  if (!s) return "";
  const ss = s.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const ee = e ? e.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
  return ee ? `${ss} - ${ee}` : ss;
};

// Reused date badge
const DateBlock = ({ dateIso }) => (
  <div className="flex-shrink-0 mr-5">
    <div className="flex flex-col items-center justify-center w-20 h-20 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 text-white shadow-md">
      <span className="text-xl font-bold -mb-1">
        {dateIso ? String(new Date(dateIso).getDate()).padStart(2, "0") : ""}
      </span>
      <span className="text-sm font-semibold uppercase">
        {dateIso ? new Date(dateIso).toLocaleString(undefined, { month: "short" }) : ""}
      </span>
      <span className="text-xs opacity-80">{dateIso ? new Date(dateIso).getFullYear() : ""}</span>
    </div>
  </div>
);

// Reservation card (aesthetic matches upcoming events cards)
const ReservationCard = ({ r }) => {
  const title = r.title || r.event?.title || "Reserved Event";
  const start = r.startDate || r.event?.startDate;
  const end = r.endDate || r.event?.endDate;
  const location = r.location || r.event?.location;

  return (
    <div className="bg-white dark:bg-navy-800 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 ease-in-out overflow-hidden p-4">
      <div className="flex items-center">
        <DateBlock dateIso={start} />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">{title}</p>
          <div className="mt-1 flex flex-wrap gap-2 text-xs text-gray-600 dark:text-gray-300">
            <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs border-gray-200 bg-gray-50 dark:border-navy-700 dark:bg-navy-700 dark:text-gray-200">
              {formatDate(start)}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs border-gray-200 bg-gray-50 dark:border-navy-700 dark:bg-navy-700 dark:text-gray-200">
              {formatTimeRange(start, end)}
            </span>
            {location && (
              <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs border-gray-200 bg-gray-50 dark:border-navy-700 dark:bg-navy-700 dark:text-gray-200">
                <FaMapMarkerAlt className="opacity-80" /> {location}
              </span>
            )}
            <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs border-gray-200 bg-gray-50 dark:border-navy-700 dark:bg-navy-700 dark:text-gray-200">
              {(r.status || "reserved").toLowerCase()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const ReservationsOnly = () => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // pagination: max 4 per page
  const RES_PER_PAGE = 4;
  const [page, setPage] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const mine = await fetchMyReservations();
        if (!alive) return;
        setReservations(Array.isArray(mine) ? mine : []);
      } catch (e) {
        if (!alive) return;
        console.error(e);
        setErr("Failed to load reservations.");
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => { setPage(0); }, [reservations]);

  // Filter to show only upcoming events
  const upcomingReservations = useMemo(() => {
    const now = new Date();
    return reservations.filter(reservation => {
      const eventDate = new Date(reservation.startDate || reservation.event?.startDate);
      return eventDate > now;
    });
  }, [reservations]);

  const totalPages = Math.max(1, Math.ceil(upcomingReservations.length / RES_PER_PAGE));
  const slice = useMemo(() => {
    const start = page * RES_PER_PAGE;
    return upcomingReservations.slice(start, start + RES_PER_PAGE);
  }, [upcomingReservations, page]);

  if (loading) {
    return (
      <Card extra={"p-4 md:p-6 flex flex-col items-center"}>
        <div className="h-6 w-52 animate-pulse rounded bg-gray-200 dark:bg-navy-700" />
        <div className="mt-4 grid gap-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-12 w-full animate-pulse rounded-xl bg-gray-100 dark:bg-navy-700" />
          ))}
        </div>
      </Card>
    );
  }
  if (err) {
    return (
      <Card extra={"p-4 md:p-6 flex flex-col items-center"}>
        <div className="text-red-600">
          {err}
        </div>
      </Card>
    );
  }

  return (
    <Card extra={"p-4 md:p-6 flex flex-col items-center"}>
      {/* Header */}
      <div className="flex flex-col items-center mb-4 w-full">
        <h2 className="text-xl md:text-2xl font-bold text-center text-navy-700 dark:text-white flex items-center gap-2 mb-2">
          <FaListUl className="text-gray-400" /> My Reservations
        </h2>
        {upcomingReservations.length > RES_PER_PAGE && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(p - 1, 0))}
              disabled={page === 0}
              className="p-2 rounded-full disabled:opacity-40 disabled:cursor-not-allowed text-navy-700 dark:text-white hover:bg-gray-200 dark:hover:bg-navy-700 transition-colors"
            >
              <FaChevronLeft />
            </button>
            <span className="text-sm font-semibold text-gray-500 dark:text-gray-400">
              {page + 1} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(p + 1, totalPages - 1))}
              disabled={page === totalPages - 1}
              className="p-2 rounded-full disabled:opacity-40 disabled:cursor-not-allowed text-navy-700 dark:text-white hover:bg-gray-200 dark:hover:bg-navy-700 transition-colors"
            >
              <FaChevronRight />
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      {slice.length === 0 ? (
        <div className="flex items-center justify-center gap-3 rounded-2xl border p-6 border-gray-200 bg-gray-50 text-gray-600 dark:border-navy-700 dark:bg-navy-900 dark:text-gray-300">
          <FaRegCalendarTimes className="text-xl" />
          {upcomingReservations.length === 0 ? 
            "You don't have any upcoming event reservations." : 
            "No more reservations to show."
          }
        </div>
      ) : (
        <div className="space-y-3 w-full">
          {slice.map((r) => (
            <ReservationCard key={r.id} r={r} />
          ))}
        </div>
      )}
    </Card>
  );
};

export default ReservationsOnly;
